export const normalized = (value: any): string => String(value ?? '').trim().toUpperCase();

export function values(value: any): string[] {
  if (Array.isArray(value)) return value.flatMap(values);
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try { return values(JSON.parse(value)); } catch { /* Keep plain text values. */ }
  }
  return value == null ? [] : [String(value)];
}

export function matches(project: any, conditions: any[] = []): boolean {
  return conditions.every(condition => {
    const actual = values(project[condition.field]).map(normalized);
    const expected = values(condition.values).map(normalized);
    return actual.some(value => expected.includes(value));
  });
}

export function extractProjects(response: any, key: string): any[] {
  if (Array.isArray(response)) return response.flatMap(item => extractProjects(item, key));
  if (!response || typeof response !== 'object') return [];
  if (response[key] !== undefined) return extractProjects(response[key], key);
  if (response.final_em !== undefined || response.cleaned_vertical !== undefined) return [response];
  return ['responseList', 'responseData', 'data'].flatMap(path => extractProjects(response[path], key));
}

export function buildPortfolio(config: any, projects: any[]): any[] {
  const fields = config.fields;
  const deleted = new Set(['TRUE', '1', 'YES']);
  const unique = new Map<string, any>();
  projects.filter(project => !deleted.has(normalized(project.is_deleted))).forEach((project, index) => {
    unique.set(String(project.uuid || `${project.source}:${project.vpcr_number || index}`), project);
  });
  const all = [...unique.values()];
  return config.markets.map((market: any) => ({
    ...market,
    cells: config.engineFamilies.map((family: any) => {
      const rows = all.filter(project =>
        values(project[fields.market]).some(value => market.values.map(normalized).includes(normalized(value))) &&
        values(project[fields.engine]).some(value => family.values.map(normalized).includes(normalized(value)))
      );
      const status = config.statuses.find((entry: any) =>
        rows.some(project => matches(project, entry.conditions))
      );
      const significant = rows.filter(project =>
        Number(project[fields.investment]) >= config.iconInvestmentThreshold ||
        (config.strategicConditions?.length && matches(project, config.strategicConditions))
      );
      const icons = config.icons.filter((icon: any) => significant.some(project => matches(project, icon.conditions)));
      return {
        market: market.label, family: family.label, projects: rows, status, icons,
        investment: rows.reduce((total, project) => total + (Number(project[fields.investment]) || 0), 0),
        label: `${market.label}, ${family.label}: ${rows.length} projects${status ? ', ' + status.label : ''}`,
      };
    }),
  }));
}

/** Uses the server's matrix classifications rather than recalculating them from projects. */
export function mapPortfolioHeatmap(config: any, response: any, mapping: any): any {
  const matrix = String(mapping.path).split('.').reduce((value, key) => value?.[key], response);
  const columns = Array.isArray(matrix?.[mapping.columns]) ? matrix[mapping.columns] : [];
  const sourceRows = Array.isArray(matrix?.[mapping.rows]) ? matrix[mapping.rows] : [];
  const engineFamilies = columns.map((column: any) => ({
    id: String(column[mapping.columnKey]),
    label: column[mapping.columnLabel] || column[mapping.columnKey],
    power: column[mapping.columnPower] || '',
  }));
  const rows = sourceRows.map((row: any) => {
    const market = row[mapping.market];
    const label = config.markets.find((entry: any) => entry.values.map(normalized).includes(normalized(market)))?.label || market;
    const sourceCells = Array.isArray(row[mapping.cells]) ? row[mapping.cells] : [];
    return {
      id: String(market), label,
      cells: engineFamilies.map((family: any) => {
        const cell = sourceCells.find((entry: any) => normalized(entry[mapping.cellKey]) === normalized(family.id)) || {};
        const projects = Array.isArray(cell[mapping.projects]) ? cell[mapping.projects] : [];
        const status = config.statuses.find((entry: any) =>
          (entry.responseValues || []).map(normalized).includes(normalized(cell[mapping.status])) ||
          (entry.responseFlag && normalized(cell[entry.responseFlag]) === 'TRUE')
        );
        const codes = values(cell[mapping.icons]).map(normalized);
        const icons = config.icons.filter((entry: any) => (entry.responseValues || []).some((code: any) => codes.includes(normalized(code))));
        const note = cell[mapping.note] || '';
        return {
          market: label, family: family.label, projects, status, icons, note,
          products: cell[mapping.products] || [],
          label: `${label}, ${family.label}${status ? ', ' + status.label : ''}${note ? ': ' + note : ''}`,
        };
      }),
    };
  });
  return { engineFamilies, rows, hasData: engineFamilies.length > 0 && rows.length > 0 };
}

/** Groups recorded needs; absence of a project is never treated as a confirmed gap. */
export function buildStrategicMap(config: any, projects: any[]): any {
  if (!config?.enabled) return { rows: [], projectCount: 0, unclassified: 0 };
  const unique = new Map<string, any>();
  projects.filter(project => !['TRUE', '1', 'YES'].includes(normalized(project.is_deleted)))
    .forEach((project, index) => unique.set(String(project.uuid || `${project.source}:${project.vpcr_number || index}`), project));
  const all = [...unique.values()];
  const driverFor = (project: any) => config.drivers.find((driver: any) =>
    values(project[config.fields.driver]).some(value => driver.values.map(normalized).includes(normalized(value)))
  );
  const groupsFor = (project: any): any[] => {
    const groups = config.groups.filter((group: any) => !group.fallback &&
      values(project[config.fields.engine]).some(value => group.values.map(normalized).includes(normalized(value)))
    );
    return groups.length ? groups : config.groups.filter((group: any) => group.fallback);
  };
  const rows = config.groups.map((group: any) => ({
    ...group,
    cells: config.drivers.map((driver: any) => {
      const matching = all.filter(project => driverFor(project)?.id === driver.id &&
        groupsFor(project).some(entry => entry.id === group.id));
      const needs = new Map<string, any>();
      matching.forEach(project => {
        const titles = values(project[config.fields.need]).filter(value => value.trim());
        (titles.length ? titles : [config.labels.missingNeed]).forEach(title => {
          const key = normalized(title);
          if (!needs.has(key)) needs.set(key, { title, markets: new Set<string>(), projects: [] });
          const need = needs.get(key);
          values(project[config.fields.market]).forEach(market => need.markets.add(market));
          if (!need.projects.includes(project)) need.projects.push(project);
        });
      });
      return {
        id: `${group.id}:${driver.id}`, driver, projects: matching,
        needs: [...needs.values()].map(need => ({ ...need, markets: [...need.markets].join(', ') })),
      };
    }),
  }));
  return { rows, projectCount: all.length, unclassified: all.filter(project => !driverFor(project)).length };
}
